import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../auth/UseUser';
import ConfirmationModal from './modals/ConfirmationModal';
import { ReactComponent as Logo } from '../assets/logo.svg';
import { ReactComponent as DraftIcon } from '../assets/MyDraftIcon.svg';
import { ReactComponent as CartIcon } from '../assets/CartIcon.svg';
import { useCart } from '../hooks/useCart';
import { useMenuItems } from '../hooks/useMenuItems';
import { useAppContext } from '../hooks/useAppContext';
import { ReactComponent as LaykaStar } from '../assets/icons/laykaStar.svg';
import { useTranslation } from 'react-i18next';

// Add this CSS to your global styles or component styles
const menuAnimations = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

function NavBar() {
	const { t } = useTranslation();
	const MemoizedLogo = Logo;
	const { setUser, isSignedIn } = useUser();
	const { selectZone } = useAppContext();
	const { cart } = useCart();

	const { menuItems } = useMenuItems();
	let navigate = useNavigate();
	const [logOutModal, setLogOutModal] = useState(false);

	const closeLogOutModal = () => {
		setLogOutModal(false);
	};

	const logout = () => {
		localStorage.clear();
		setUser({});
		selectZone({});
	};

	const navigateToLogin = () => {
		navigate('/login');
	};

	return (
		<>
			<style>{menuAnimations}</style>
			<div className="w-full bg-[#fff]">
				<div className="max-w-[1280px] w-full mx-auto h-[92px] flex justify-between items-center px-[20px] bg-[#fff] text-black">
					<div className="w-fit h-5 justify-center items-center gap-[30px] hidden sm:inline-flex opacity-0 pointer-events-none">
						{isSignedIn ? (
							<button onClick={() => navigate('/profile/user')} className="text-black text-sm font-normal leading-[18px] transition-all duration-[230ms]">
								{t('Profile')}
							</button>
						) : (
							<button className="text-black text-sm font-normal leading-[18px] transition-all duration-[230ms]" onClick={navigateToLogin}>
								{t('Log in')}
							</button>
						)}

						{isSignedIn && (
							<div className="justify-start items-center gap-2 flex">
								<button className="text-black text-sm font-normal leading-[18px] flex flex-row items-center justify-center gap-[8px] transition-all duration-[230ms]" onClick={() => navigate('/drafts')}>
									<DraftIcon />
									<span>{t('My draft')}</span>
								</button>
							</div>
						)}
						<div className="justify-start items-center gap-2 flex">
							<button className="text-black text-sm font-normal leading-[18px] flex flex-row items-center justify-center gap-[8px] transition-all duration-[230ms]" onClick={() => navigate('/cart')}>
								<CartIcon />
								<span>({cart.length})</span>
							</button>
						</div>
					</div>
					<div className="cursor-pointer w-full md:w-auto flex items-center" onClick={() => navigate('')} title="Layka Gift">
						<MemoizedLogo className="mx-auto" />
					</div>

					<div className="w-fit h-5 justify-center items-center gap-[30px] hidden sm:inline-flex">
						{isSignedIn ? (
							<button onClick={() => navigate('/profile/user')} className="text-black text-sm font-normal leading-[18px] transition-all duration-[230ms]">
								{t('Profile')}
							</button>
						) : (
							<button className="text-black text-sm font-normal leading-[18px] transition-all duration-[230ms]" onClick={navigateToLogin}>
								{t('Log in')}
							</button>
						)}

						{isSignedIn && (
							<div className="justify-start items-center gap-2 flex">
								<button className="text-black text-sm font-normal leading-[18px] flex flex-row items-center justify-center gap-[8px] transition-all duration-[230ms]" onClick={() => navigate('/drafts')}>
									<DraftIcon />
									<span>{t('My draft')}</span>
								</button>
							</div>
						)}
						<div className="justify-start items-center gap-2 flex">
							<button className="text-black text-sm font-normal leading-[18px] flex flex-row items-center justify-center gap-[8px] transition-all duration-[230ms]" onClick={() => navigate('/cart')}>
								<CartIcon />
								<span>({cart.length})</span>
							</button>
						</div>
					</div>
				</div>
				<div className="min-h-[26px] bg-[#fff] items-center gap-y-1 sm:gap-x-10 xl:gap-x-20 gap-x-5 pt-[2px] flex-wrap border-y border-y-babyBlue hidden md:flex">
					<div className="xl:max-w-[1280px] w-full mx-auto justify-between items-center gap-[10px] inline-flex px-[20px]">
						{menuItems.map((menuItem: any, index: number) => (
							<MenuButton item={menuItem} key={menuItem.id} index={index} />
						))}
					</div>
				</div>
				<ConfirmationModal modalIsOpen={logOutModal} closeModal={closeLogOutModal} confirmation={logout} message={t(`Logout`)} />
			</div>
		</>
	);
}

const MenuButton = ({ item, index }: { item: any; index: number }) => {
	const [hover, setHover] = useState(false);
	const navigate = useNavigate();
	const ref = useRef<any>(null);
	const [leftPosition, setLeftPosition] = useState('');
	const showSubmenu = hover && !!item?.subCategories.length;

	const navigateToProductType = (type: string, subCategory = null) => {
		navigate(`/products/${type}${!!subCategory ? `/${subCategory}` : ''}`.toLowerCase());
	};

	useEffect(() => {
		if (ref.current) {
			const { left, right } = ref?.current?.getBoundingClientRect();
			if (left <= 100) {
				setLeftPosition('left-[-20px]');
			} else if (window.innerWidth - right <= 100) {
				setLeftPosition('right-[-20px]');
			} else {
				setLeftPosition('-translate-x-1/2 left-1/2');
			}
		}
	}, [showSubmenu]);

	return (
		<div className="relative" onMouseLeave={() => setHover(false)} ref={ref}>
			<button
				onMouseEnter={() => setHover(true)}
				className={`${hover ? 'text-laykaPink' : 'text-black'} text-[0.875rem] xl:text-base font-medium uppercase leading-normal cursor-pointer flex flex-row items-center gap-[6px] h-[50px] transition-colors duration-300`}
				onClick={() => navigateToProductType(item?.seo?.urlSlug)}
			>
				{item.featured && <LaykaStar />}
				<span>{item.name}</span>
				{item.featured && <LaykaStar />}
			</button>

			{!!item?.subCategories.length && (
				<div
					className={`absolute top-[50px] w-[216px] z-[5] p-[20px] items-start flex flex-col gap-[16px] rounded-bl-[12px] rounded-br-[12px] bg-white border border-[#F1F1FE] shadow-lg ${showSubmenu ? 'visible' : 'invisible'} ${leftPosition}`}
					style={{
						opacity: showSubmenu ? 1 : 0,
						transition: 'opacity 300ms ease-in-out, visibility 300ms ease-in-out, transform 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
						transformOrigin: 'top center',
					}}
				>
					{item?.subCategories.map((subItem: any, subIndex: number) => (
						<button
							key={subItem.id}
							className="text-black hover:text-laykaPink text-[1rem] font-medium leading-normal cursor-pointer w-full text-left transition-colors duration-200"
							style={{
								opacity: 0,
								animation: showSubmenu ? `fadeIn 300ms ease-out forwards ${subIndex * 60}ms` : 'none',
							}}
							onClick={() => navigateToProductType(item?.seo?.urlSlug, subItem?.seo?.urlSlug)}
						>
							{subItem.name}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default NavBar;
